import {useCallback, useState} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {enqueueSnackbar} from 'notistack';
import {IStation} from '../../../../../dto/Station.dto';
import {DialogClose} from '../../../../../components/dialogs/DialogFrame/DialogClose';
import {apiPost} from '../../../../../axiosConfig';
import {useQueryClient} from '@tanstack/react-query';

const MirrorThisStation = ({station}: {station: IStation}) => {
  const [open, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <>
      <Button variant='contained' disabled={open} onClick={handleClick}>
        Mirror This Station
      </Button>
      {open && (
        <MirrorStationDialog station={station} onClose={() => setOpen(false)} />
      )}
    </>
  );
};

const MirrorStationDialog = ({
  station,
  onClose,
}: {
  station: IStation;
  onClose: () => void;
}) => {
  const [name, setName] = useState({
    value: `CMC ${station.name}`,
    error: false,
    helper: '',
  });
  const [apiState, setApiState] = useState({status: '', error: ''});
  const [expireDays, setExpireDays] = useState(1);
  const queryClient = useQueryClient();
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nameObj = {value: e.target.value, helper: '', error: false};
      if (!e.target.value) {
        Object.assign(nameObj, {helper: 'Required', error: true});
      }
      setName(nameObj);
      setApiState({status: '', error: ''});
    },
    [],
  );
  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      apiPost(
        `/v1/company/${station.company_id}/station/${station.id}/mirror`,
        {name: name.value, days: expireDays},
      )
        .then(res => {
          enqueueSnackbar(res.data.message, {variant: 'success'});
          onClose();
          queryClient.invalidateQueries({
            queryKey: ['api/v1/company', station.company_id, 'stations'],
            refetchType: 'active',
          });
        })
        .catch(error => {
          let validationMsgSet = false;
          // Validation failed
          if (error.response?.data?.errors && error.response.data.errors.name) {
            setName(prevVal => ({
              ...prevVal,
              error: true,
              helper: error.response.data.errors.name[0],
            }));
            validationMsgSet = true;
          }
          setApiState({
            status: validationMsgSet ? '' : 'error',
            error: validationMsgSet
              ? ''
              : error.response?.data.message || error.message,
          });
        });
    },
    [
      expireDays,
      name.value,
      onClose,
      queryClient,
      station.company_id,
      station.id,
    ],
  );
  return (
    <Dialog fullWidth maxWidth='sm' open onClose={onClose} component={'form'}>
      <DialogTitle className='flex justify-between'>
        <Typography>Mirror Station: {station.label || station.name}</Typography>
        <DialogClose onClose={onClose} />
      </DialogTitle>
      <DialogContent>
        <TextField
          name='station_name'
          label='Station Name'
          value={name.value}
          onChange={handleChange}
          error={name.error}
          helperText={name.helper}
          required
          fullWidth
          margin='normal'
          className='inputLabel-shink-background'
        />
        <FormControl margin='normal' fullWidth>
          <InputLabel id='expiry-label' className='inputLabel-shink-background'>
            Expire After
          </InputLabel>
          <Select
            labelId='expiry-label'
            id='expiry'
            value={expireDays}
            label='Expire After'
            SelectDisplayProps={{
              role: 'button',
              'aria-haspopup': 'listbox',
              'aria-controls': undefined,
            }}
            onChange={e => setExpireDays(+e.target.value)}>
            <MenuItem value={1}>1 Day</MenuItem>
            <MenuItem value={2}>2 Days</MenuItem>
            <MenuItem value={3}>3 Days</MenuItem>
            <MenuItem value={4}>4 Days</MenuItem>
            <MenuItem value={5}>5 Days</MenuItem>
            <MenuItem value={6}>6 Days</MenuItem>
            <MenuItem value={7}>7 Days</MenuItem>
          </Select>
          <FormHelperText>
            This station will be expired after selected days.
          </FormHelperText>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          disabled={apiState.status === 'pending' || name.error}
          onClick={handleSubmit}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MirrorThisStation;
